import React, { useEffect, useRef, useState } from 'react';

/**
 * Sliding detail drawer for the currently selected department.
 *
 * Same UX pattern as UserDetailDrawer: slide-in from the right with focus
 * management, Esc-to-close, and a retained-content cross-fade on swap so the
 * drawer body doesn't blank between selections.
 *
 * Props:
 *   department      selected department object | null
 *   onClose         () => void
 *   children        drawer body content
 */
export default function DepartmentDetailDrawer({ department, onClose, children }) {
  const closeBtnRef = useRef(null);
  const lastFocusRef = useRef(null);
  const [retained, setRetained] = useState(department);
  const isOpen = !!department;

  useEffect(() => {
    if (department) setRetained(department);
  }, [department]);

  useEffect(() => {
    if (!isOpen) return;
    lastFocusRef.current = document.activeElement;
    closeBtnRef.current?.focus();
    return () => {
      const el = lastFocusRef.current;
      if (el && typeof el.focus === 'function') el.focus();
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  return (
    <>
      <div
        className={`ut-drawer-backdrop ${isOpen ? 'is-open' : ''}`}
        aria-hidden="true"
        onClick={onClose}
      />
      <aside
        className={`ut-drawer ${isOpen ? 'is-open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={retained ? `Details for ${retained.name}` : 'Department details'}
        aria-hidden={!isOpen}
      >
        <header
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '16px 20px',
            borderBottom: '1px solid #e2e8f0',
            position: 'sticky',
            top: 0,
            background: 'white',
            zIndex: 1,
          }}
        >
          <button
            ref={closeBtnRef}
            type="button"
            onClick={onClose}
            aria-label="Close department details"
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              fontSize: 22,
              lineHeight: 1,
              color: '#4a5568',
            }}
          >
            ×
          </button>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#2d3748', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {retained?.name}
            </div>
            {retained?.description && (
              <div style={{ fontSize: 12, color: '#718096', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {retained.description}
              </div>
            )}
          </div>
        </header>
        <div className="ut-drawer-body">
          {retained ? children : null}
        </div>
      </aside>
    </>
  );
}
